import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, FileSignature, Loader2, Mail, ShieldAlert, ShieldCheck } from "lucide-react";
import { SfPdfPages } from "../components/allegatistudio/SfPdfPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/firma`;
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : "Operazione non riuscita, riprova";
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" }) : "");
const BTN = "inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 font-semibold px-5 h-11 transition-colors";
const INP = "w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50";

const PdfView = ({ token, version }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    axios.get(`${API}/${token}/pdf`, { responseType: "blob" }).then(({ data }) => { u = URL.createObjectURL(data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [token, version]);
  if (!url) return <div className="h-[60vh] grid place-items-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  return (
    <div className="space-y-2">
      <div className="h-[65vh] rounded-lg border border-slate-200 bg-slate-200/60" data-testid="firma-pdf"><SfPdfPages url={url} /></div>
      <a href={url} download="documento.pdf" className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:underline" data-testid="firma-pdf-download"><Download className="h-4 w-4" /> Scarica il documento</a>
    </div>
  );
};

const Steps = ({ token, info, onSigned }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [session, setSession] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };

  const sendOtp = () => run(async () => { await axios.post(`${API}/${token}/otp`, { email: email.trim() }); setSent(true); });
  const verify = () => run(async () => { const { data } = await axios.post(`${API}/${token}/verifica`, { code: code.trim() }); setSession(data.session); });
  const accept = () => run(async () => { await axios.post(`${API}/${token}/accetta`, { session, accetto: true }); onSigned(); });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 shadow-sm" data-testid="firma-steps">
      {!session ? (
        <>
          <div className="flex items-center gap-2 font-semibold text-slate-900"><Mail className="h-5 w-5 text-amber-600" /> 1. Verifica la tua email</div>
          <p className="text-sm text-slate-600">Inserisci l'email a cui hai ricevuto il link ({info.email}): ti invieremo un codice di verifica.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.it" className={INP} data-testid="firma-email" />
            <button type="button" onClick={sendOtp} disabled={busy || !email.includes("@")} className={`${BTN} shrink-0`} data-testid="firma-send-otp">{busy && !sent ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{sent ? "Invia di nuovo" : "Invia codice"}</button>
          </div>
          {sent && (
            <>
              <p className="text-sm text-emerald-700" data-testid="firma-otp-sent">Codice inviato. Controlla la posta (anche lo spam): è valido 10 minuti.</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="Codice a 6 cifre" className={`${INP} font-mono tracking-[0.4em]`} data-testid="firma-otp" />
                <button type="button" onClick={verify} disabled={busy || code.length !== 6} className={`${BTN} shrink-0`} data-testid="firma-verify-otp">Verifica</button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 font-semibold text-slate-900"><ShieldCheck className="h-5 w-5 text-emerald-600" /> 2. Accetta e firma</div>
          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5 h-5 w-5 accent-amber-500" data-testid="firma-accetto" />
            <span>Dichiaro di aver letto il documento "<strong>{info.documento}</strong>" e di accettarlo integralmente. La mia accettazione, con data, ora e indirizzo IP, varrà come firma.</span>
          </label>
          <button type="button" onClick={accept} disabled={busy || !ok} className={`${BTN} w-full`} data-testid="firma-accetta">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />} Accetto e firmo</button>
        </>
      )}
      {err && <p className="text-sm text-red-600" data-testid="firma-error">{err}</p>}
    </div>
  );
};

export default function FirmaPublic() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState("");
  const [version, setVersion] = useState(0);
  const load = () => axios.get(`${API}/${token}`).then(({ data }) => setInfo(data)).catch((e) => setFatal(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900" data-testid="firma-public-page">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">
        {fatal ? (
          <div className="rounded-xl bg-white border border-slate-200 p-8 text-center space-y-2" data-testid="firma-fatal">
            <ShieldAlert className="h-10 w-10 text-red-500 mx-auto" /><p className="text-slate-700">{fatal}</p>
          </div>
        ) : !info ? (
          <div className="grid place-items-center h-64"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <div>
              <p className="text-sm text-slate-500">{info.studio}</p>
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="firma-documento">{info.documento}</h1>
              {!info.firmato && <p className="text-sm text-slate-500 mt-1">Leggi il documento qui sotto, poi verifica la tua email per firmarlo. Link valido fino al {fmt(info.expires_at)}.</p>}
            </div>
            {info.firmato && info.completo && (
              <div className="flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-emerald-800" data-testid="firma-done">
                <CheckCircle2 className="h-6 w-6 shrink-0" /> {info.congiunta ? "Documento firmato da tutti i firmatari." : `Documento firmato il ${fmt(info.signed_at)}.`} Una copia è stata salvata dallo studio; puoi scaricarla qui sotto.
              </div>
            )}
            {info.firmato && !info.completo && (
              <div className="flex items-center gap-3 rounded-xl border border-sky-300 bg-sky-50 p-4 text-sky-800" data-testid="firma-attesa-altri">
                <CheckCircle2 className="h-6 w-6 shrink-0" /> Hai firmato il {fmt(info.signed_at)}. Il documento sarà valido quando firmerà anche {(info.altri || []).filter((a) => !a.firmato).map((a) => a.firmatario).join(", ") || "l'altro firmatario"}: riapri questo link per scaricare la copia firmata.
              </div>
            )}
            {info.congiunta && !info.firmato && (
              <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600" data-testid="firma-congiunta-info">
                Firma congiunta: il documento va firmato anche da {(info.altri || []).map((a) => `${a.firmatario}${a.firmato ? " (ha già firmato)" : ""}`).join(", ")}. Sarà valido solo quando avranno firmato tutti.
              </div>
            )}
            {info.privacy && !info.firmato && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="firma-privacy-info">
                In fondo al documento trovi l'informativa sul trattamento dei dati personali: firmando dichiari di averla letta e acconsenti al trattamento.
              </div>
            )}
            <PdfView token={token} version={version} />
            {!info.firmato && <Steps token={token} info={info} onSigned={() => { setVersion((v) => v + 1); load(); }} />}
          </>
        )}
      </div>
    </div>
  );
}
